/**
 * Morse-Code: Zeichentabelle, Zeitplan und Dekoder. (06.10.2026, Morse-Modul Bahn 1)
 *
 * Martin möchte Morse lernen (Koch-Methode, eigene Seite) — und später soll
 * Vespera selbst morsen. Diese Datei ist der EINE Besitzer der Regeln, die
 * Seite, Ton-Spieler und Server gleich rechnen müssen:
 *
 *   - die Tabelle (A–Z, Ziffern, Satzzeichen, Ä Ö Ü ß, „CH“);
 *   - der Zeitplan nach der PARIS-Norm: ein Punkt dauert 1200 / Tempo ms,
 *     ein Strich drei Punkte, zwischen den Elementen eines Zeichens ein
 *     Punkt Pause, zwischen Zeichen drei, zwischen Wörtern sieben;
 *   - die Farnsworth-Dehnung: die Zeichen selbst kommen im vollen Tempo
 *     (so lernt man den Klang, nicht das Zählen), nur die Pausen werden
 *     länger, bis das WIRKSAME Tempo stimmt (Formel der ARRL);
 *   - der Dekoder für das Geben: er schätzt die Punktlänge aus der Eingabe
 *     selbst und folgt ihr — Menschen geben nie im Takt einer Uhr.
 *
 * Rein: kein DOM, kein Ton, kein Speicher. Alles hier ist ohne Gerät prüfbar.
 */

/**
 * Die Tabelle. Schlüssel: das Zeichen in Großbuchstaben; „ß“ und „CH“ sind
 * eigene Einträge (das deutsche „ß“ ist SZ, ...--..).
 *
 * ⚠️ „ß“ NICHT über toUpperCase() suchen: das macht daraus „SS“.
 */
export const MORSE_TABELLE: Readonly<Record<string, string>> = {
  A: ".-", B: "-...", C: "-.-.", D: "-..", E: ".", F: "..-.", G: "--.", H: "....", I: "..",
  J: ".---", K: "-.-", L: ".-..", M: "--", N: "-.", O: "---", P: ".--.", Q: "--.-", R: ".-.",
  S: "...", T: "-", U: "..-", V: "...-", W: ".--", X: "-..-", Y: "-.--", Z: "--..",
  "0": "-----", "1": ".----", "2": "..---", "3": "...--", "4": "....-",
  "5": ".....", "6": "-....", "7": "--...", "8": "---..", "9": "----.",
  ".": ".-.-.-", ",": "--..--", "?": "..--..", "/": "-..-.", "=": "-...-", "'": ".----.",
  "!": "-.-.--", "(": "-.--.", ")": "-.--.-", "&": ".-...", ":": "---...", ";": "-.-.-.",
  "+": ".-.-.", "-": "-....-", _: "..--.-", '"': ".-..-.", "@": ".--.-.",
  Ä: ".-.-", Ö: "---.", Ü: "..--", ß: "...--..", CH: "----",
};

/** Umgekehrt: Muster → Zeichen. Jedes Muster kommt in der Tabelle genau einmal vor (Test). */
export const MORSE_RUECKWAERTS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(MORSE_TABELLE).map(([z, m]) => [m, z])),
);

/** Was der Dekoder für ein Muster schreibt, das es nicht gibt. */
export const UNBEKANNT = "*";

/** Ein Zeichen in Tabellen-Schreibweise (Großbuchstaben, „ß“ bleibt „ß“). */
export function grossZeichen(z: string): string {
  return z === "ß" ? "ß" : z.toLocaleUpperCase("de-DE");
}

/** Das Muster eines Zeichens, oder `null`. */
export function morseMuster(zeichen: string): string | null {
  return MORSE_TABELLE[grossZeichen(zeichen)] ?? null;
}

interface TextZerlegung {
  /** Je Wort die Zeichen in Tabellen-Schreibweise. */
  woerter: string[][];
  /** Was nicht in der Tabelle steht (je einmal, in der Reihenfolge des Auftretens). */
  unbekannt: string[];
}

/**
 * Text → Wörter aus Zeichen der Tabelle. Leerraum trennt Wörter; Zeichen ohne
 * Muster werden ausgelassen und gemeldet.
 *
 * `ch`: „ch“ als EIN Zeichen (----) geben — so stand es früher in deutschen
 * Prüfungen. Vorgabe aus: im Funkbetrieb wird C H gegeben, und die
 * Koch-Reihenfolge kennt kein „CH“.
 */
export function zerlegeText(text: string, o: { ch?: boolean } = {}): TextZerlegung {
  const unbekannt: string[] = [];
  const woerter: string[][] = [];
  for (const wort of text.normalize("NFC").split(/\s+/u)) {
    const zeichen: string[] = [];
    const teile = [...wort];
    for (let i = 0; i < teile.length; i++) {
      const z = grossZeichen(teile[i]!);
      if (o.ch && z === "C" && grossZeichen(teile[i + 1] ?? "") === "H") {
        zeichen.push("CH");
        i++;
        continue;
      }
      if (MORSE_TABELLE[z] !== undefined) zeichen.push(z);
      else if (!unbekannt.includes(teile[i]!)) unbekannt.push(teile[i]!);
    }
    if (zeichen.length) woerter.push(zeichen);
  }
  return { woerter, unbekannt };
}

/**
 * Ein Muster als Silben, wie man Morse spricht: ein Strich ist „dah“, ein Punkt „di“ — als letztes Element eines
 * Zeichens „dit“ (dort klingt der Punkt aus). A = di-dah, K = dah-di-dah, C = dah-di-dah-dit, E = dit.
 * (06.10.2026, Morse-Tabelle) Aus dem Muster gerechnet, nie von Hand getippt — der Test prüft die ganze Tabelle.
 */
export function musterSilben(muster: string): string {
  const e = [...muster].filter((x) => x === "." || x === "-");
  return e.map((x, i) => (x === "-" ? "dah" : i === e.length - 1 ? "dit" : "di")).join("-");
}

/** Text als Muster, Zeichen durch Leerzeichen, Wörter durch „ / “ getrennt (zum Anzeigen). */
export function textAlsMuster(text: string, o: { ch?: boolean } = {}): string {
  return zerlegeText(text, o)
    .woerter.map((w) => w.map((z) => MORSE_TABELLE[z]).join(" "))
    .join(" / ");
}

/* ── Tempo und Zeitplan ──────────────────────────────────────────────── */

/** Grenzen für Tempo (Wörter pro Minute nach PARIS) und Tonhöhe. */
export const MORSE_GRENZEN = {
  tempoMin: 5,
  tempoMax: 40,
  tonMin: 300,
  tonMax: 1000,
} as const;

/** Die Vorgaben aus dem Plan: 20 WpM Zeichentempo, wirksam 10, Ton 600 Hz. */
export const MORSE_VORGABE = { tempo: 20, wirksam: 10, ton: 600 } as const;

export interface MorseEinstellungen {
  /** Zeichentempo in WpM (PARIS) — so schnell klingt jedes Zeichen. */
  tempo: number;
  /** Wirksames Tempo in WpM — die Pausen werden gedehnt, bis es stimmt. Nie über `tempo`. */
  wirksam: number;
  /** Tonhöhe in Hz. */
  ton: number;
}

/**
 * Eingaben auf gültige Einstellungen bringen: Zahlen gerundet und in die
 * Grenzen gezogen, `wirksam` nie über `tempo`. Unsinn wird zur Vorgabe.
 * Seite UND Server rufen dasselbe — sonst speichert der Server, was die Seite
 * nie zeigen würde.
 */
export function pruefeMorseEinstellungen(roh: unknown): MorseEinstellungen {
  const r = (roh && typeof roh === "object" ? roh : {}) as Record<string, unknown>;
  const zahl = (v: unknown, vorgabe: number, min: number, max: number): number =>
    typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : vorgabe;
  const { tempoMin, tempoMax, tonMin, tonMax } = MORSE_GRENZEN;
  const tempo = zahl(r.tempo, MORSE_VORGABE.tempo, tempoMin, tempoMax);
  const wirksam = Math.min(tempo, zahl(r.wirksam, Math.min(tempo, MORSE_VORGABE.wirksam), tempoMin, tempoMax));
  const ton = zahl(r.ton, MORSE_VORGABE.ton, tonMin, tonMax);
  return { tempo, wirksam, ton };
}

/** „Langsam“ heißt 60 % des eigenen Tempos (Fehler noch einmal, Kennenlernen, Hörspur) — Zeichen UND Pausen. */
const MORSE_LANGSAM_ANTEIL = 0.6;

/**
 * Das langsame Tempo zu den Einstellungen: Zeichentempo und wirksames Tempo je auf 60 %, nie unter die Grenze,
 * `wirksam` nie über `tempo`. EINE Rechnung für den Ton der Seite (morse-view.ts) und die Hörspur
 * (morse-hoerspur-liste.ts) — sonst klänge „langsam“ in der Vorführung anders als beim Kennenlernen.
 */
export function langsamesTempo(e: Pick<MorseEinstellungen, "tempo" | "wirksam">): { tempo: number; wirksam: number } {
  const tempo = Math.max(MORSE_GRENZEN.tempoMin, Math.round(e.tempo * MORSE_LANGSAM_ANTEIL));
  const wirksam = Math.min(tempo, Math.max(MORSE_GRENZEN.tempoMin, Math.round(e.wirksam * MORSE_LANGSAM_ANTEIL)));
  return { tempo, wirksam };
}

/** Länge eines Punkts in ms nach PARIS (50 Einheiten je Wort). */
export function punktMs(tempo: number): number {
  return 1200 / tempo;
}

export interface Pausen {
  punktMs: number;
  /** Pause innerhalb eines Zeichens (immer ein Punkt). */
  elementMs: number;
  /** Pause zwischen zwei Zeichen. */
  zeichenMs: number;
  /** Pause zwischen zwei Wörtern. */
  wortMs: number;
}

/**
 * Die Pausen bei Zeichentempo `tempo` und wirksamem Tempo `wirksam`.
 *
 * Farnsworth nach der ARRL: PARIS hat 31 Einheiten Ton und Pausen innerhalb
 * der Zeichen und 19 Einheiten Pause zwischen Zeichen und Wörtern. Bei
 * wirksamem Tempo s dauert das Wort 60/s Sekunden; die Zeichen behalten ihr
 * Tempo c (37,2/c Sekunden), der Rest — (60c − 37,2s)/(cs) — verteilt sich im
 * Verhältnis 12 : 7 auf vier Zeichen- und eine Wortpause: je Zeichenpause
 * 3/19, je Wortpause 7/19.
 */
export function pausen(tempo: number, wirksam: number = tempo): Pausen {
  const u = punktMs(tempo);
  if (!(wirksam < tempo)) return { punktMs: u, elementMs: u, zeichenMs: 3 * u, wortMs: 7 * u };
  const restMs = ((60 * tempo - 37.2 * wirksam) / (tempo * wirksam)) * 1000;
  return { punktMs: u, elementMs: u, zeichenMs: (3 * restMs) / 19, wortMs: (7 * restMs) / 19 };
}

export interface Ton {
  /** Beginn ab Anfang des Plans, ms. */
  beginnMs: number;
  dauerMs: number;
  /** Zu welchem Zeichen (Index in `zeichen`) der Ton gehört. */
  zeichen: number;
}

export interface ZeitPlan {
  toene: Ton[];
  /** Je Zeichen: das Zeichen, sein Muster und wann es beginnt (für Anzeige und Licht). */
  zeichen: Array<{ zeichen: string; muster: string; beginnMs: number }>;
  /** Vom ersten Tonbeginn bis zum Ende des letzten Tons. */
  gesamtMs: number;
  pausen: Pausen;
  unbekannt: string[];
}

/**
 * Text → Zeitplan. Der Plan beginnt bei 0 mit dem ersten Ton und endet mit
 * dem letzten — keine Pause davor oder danach (die setzt der Spieler).
 */
export function zeitplan(text: string, e: Pick<MorseEinstellungen, "tempo" | "wirksam">, o: { ch?: boolean } = {}): ZeitPlan {
  const p = pausen(e.tempo, e.wirksam);
  const { woerter, unbekannt } = zerlegeText(text, o);
  const toene: Ton[] = [];
  const zeichen: ZeitPlan["zeichen"] = [];
  let t = 0;
  woerter.forEach((wort, wi) => {
    if (wi > 0) t += p.wortMs;
    wort.forEach((z, zi) => {
      if (zi > 0) t += p.zeichenMs;
      const muster = MORSE_TABELLE[z]!;
      zeichen.push({ zeichen: z, muster, beginnMs: t });
      [...muster].forEach((el, ei) => {
        if (ei > 0) t += p.elementMs;
        const dauerMs = el === "." ? p.punktMs : 3 * p.punktMs;
        toene.push({ beginnMs: t, dauerMs, zeichen: zeichen.length - 1 });
        t += dauerMs;
      });
    });
  });
  return { toene, zeichen, gesamtMs: t, pausen: p, unbekannt };
}

/* ── Dekoder (Geben) ─────────────────────────────────────────────────── */

/** So viele der letzten Tastendrücke schätzen die Punktlänge. */
const DEKODER_FENSTER = 12;
/** Ab diesem Verhältnis zweier Drückgruppen gelten sie als Punkt und Strich. */
const DEKODER_TRENNUNG = 1.8;
/**
 * Die Grenzen in Punkten: Punkt/Strich und Pause im Zeichen/zwischen Zeichen
 * bei √3 (zwischen 1 und 3), Zeichen/Wort bei √21 (zwischen 3 und 7).
 *
 * ⚠️ Das GEOMETRISCHE Mittel, nicht 2 und 5: eine Hand zittert im Verhältnis
 *    (ein Strich ist mal 20 % zu kurz, eine Pause 20 % zu lang), nicht um
 *    feste Millisekunden. Mit 5 lag eine um 25 % zu kurze Wortpause (5,25
 *    Punkte) nur 5 % über der Grenze, eine zu lange Zeichenpause (3,75) aber
 *    33 % darunter — die Probe mit ±25 % riss genau dort („HAT ES“ → „HATES“).
 */
const DEKODER_ZEICHEN_GRENZE = Math.sqrt(3);
const DEKODER_WORT_GRENZE = Math.sqrt(21);
/** Kürzer als das ist ein Prellen, kein Druck. */
const DEKODER_PRELLEN_MS = 15;

/** Ein Druck über so vielen Punkten der bisherigen Schätzung kann ein Ausreißer sein. */
const DEKODER_AUSREISSER_PUNKTE = 6;

/**
 * Einen einzelnen, extrem langen Druck aus der Schätzung nehmen (Prüfbefund W3b).
 *
 * Ein Anfänger hält die Taste einmal viel zu lange (10 Punkte statt 3). Ohne
 * diese Regel lag die größte Verhältnis-Lücke dann zwischen Strich und
 * Ausreißer — Striche galten als Punkte, die Schätzung verdoppelte sich. Raus
 * fällt nur der LÄNGSTE Druck, und nur, wenn er über
 * `DEKODER_AUSREISSER_PUNKTE` der bisherigen Schätzung UND über dem Doppelten
 * des zweitlängsten liegt; erst ab vier Drücken. Zwei gleich lange gelten als
 * Absicht. Sortierte Eingabe.
 */
function ohneAusreisser(d: number[], bisher: number): number[] {
  if (d.length < 4) return d;
  const groesster = d[d.length - 1]!;
  const zweiter = d[d.length - 2]!;
  return groesster > DEKODER_AUSREISSER_PUNKTE * bisher && groesster > 2 * zweiter ? d.slice(0, -1) : d;
}

/**
 * Die Punktlänge aus den letzten Drückdauern schätzen.
 *
 * Sortiert, an der größten VERHÄLTNIS-Lücke geteilt: liegen beide Seiten um
 * mindestens `DEKODER_TRENNUNG` auseinander, sind es Punkte und Striche, und
 * die Schätzung ist das gewichtete Mittel aus „Punkt“ und „Strich / 3“. Gibt
 * es keine solche Lücke (nur Punkte oder nur Striche), bleibt die bisherige
 * Schätzung — eine Reihe gleicher Drücke sagt nichts über die Länge eines
 * Punkts.
 */
export function schaetzePunktMs(druecke: readonly number[], bisher: number): number {
  const d = ohneAusreisser(
    druecke.slice(-DEKODER_FENSTER).filter((x) => Number.isFinite(x) && x > 0).sort((a, b) => a - b),
    bisher,
  );
  if (d.length < 2) return bisher;
  let teil = -1;
  let besteLuecke = 0;
  for (let i = 1; i < d.length; i++) {
    const v = d[i]! / d[i - 1]!;
    if (v > besteLuecke) {
      besteLuecke = v;
      teil = i;
    }
  }
  if (besteLuecke < DEKODER_TRENNUNG) return bisher;
  const kurz = d.slice(0, teil);
  const lang = d.slice(teil);
  const mittel = (x: number[]): number => x.reduce((a, b) => a + b, 0) / x.length;
  return (mittel(kurz) * kurz.length + (mittel(lang) / 3) * lang.length) / d.length;
}

/** Bis zu so vielen Punkten kann eine Pause noch IM Zeichen liegen (Anfänger lassen gut zwei). */
const DEKODER_PAUSE_IM_ZEICHEN_HOECHSTENS = 2.6;
/** Ab diesem Verhältnis zweier benachbarter Pausen kommt die Lücke als gelernte Zeichengrenze in Frage. */
const DEKODER_LERN_LUECKE = 1.5;

/**
 * Zwei Gruppen in einer sortierten Reihe (Otsu im Logarithmus): die Teilung mit
 * der größten Streuung zwischen den Gruppen. Gibt die geometrischen Mittel
 * beider Gruppen zurück — oder `null` bei weniger als zwei Werten.
 */
function zweiGruppen(d: readonly number[]): { unten: number; oben: number } | null {
  if (d.length < 2) return null;
  const l = d.map((x) => Math.log(x));
  let beste: { unten: number; oben: number; wert: number } | null = null;
  for (let i = 1; i < l.length; i++) {
    const a = l.slice(0, i);
    const b = l.slice(i);
    const ma = a.reduce((x, y) => x + y, 0) / a.length;
    const mb = b.reduce((x, y) => x + y, 0) / b.length;
    const wert = a.length * b.length * (mb - ma) ** 2;
    if (!beste || wert > beste.wert) beste = { unten: Math.exp(ma), oben: Math.exp(mb), wert };
  }
  return beste;
}

/**
 * Die Grenzen für Pausen (ms): ab `zeichen` endet ein Zeichen, ab `wort` ein Wort.
 *
 * Vorgabe nach der Norm: √(3·Dehnung) Punkte für das Zeichen (bei Farnsworth
 * liegen die Zeichenpausen weiter auseinander), √21·Dehnung für das Wort.
 *
 * ⚠️ Prüfbefund W3a: ein Anfänger lässt IM Zeichen oft zwei Punkte Pause —
 *    mit der festen Grenze von √3 zerriss das Zeichen. Darum lernt der
 *    Dekoder die Grenzen aus den gemessenen Pausen, wie die Punktlänge aus den
 *    Drücken:
 *    - Zeichen: in Frage kommen die Vorgabe und jede Lücke (ab
 *      `DEKODER_LERN_LUECKE`) zwischen zwei benachbarten Pausen, deren untere
 *      höchstens `DEKODER_PAUSE_IM_ZEICHEN_HOECHSTENS` Punkte lang ist (die
 *      Grenze im geometrischen Mittel der beiden). Gewählt wird, was beim
 *      Lesen die wenigsten unbekannten Zeichen ergibt (`unbekannt`), bei
 *      Gleichstand die deutlichste Lücke. Das hält beide Hände auseinander:
 *      beim Anfänger (2 Punkte im Zeichen, 4 dazwischen) ist die Lücke 2 → 4
 *      die deutlichste; bei einer zittrigen Hand nach der Norm, deren
 *      Zeichenpausen knapp unter 2,6 Punkten liegen, ergäbe die Lücke zur
 *      Wortpause zusammengeklebte Zeichen, die es nicht gibt.
 *    - Wort: die Pausen ab der Zeichengrenze in zwei Gruppen geteilt; liegen
 *      deren Mittel um `DEKODER_TRENNUNG` auseinander, sind es Zeichen- und
 *      Wortpausen, die Grenze liegt dazwischen. Liegen sie wenigstens 2 : 1
 *      auseinander, trennt die größte Lücke. Sonst sind es nur Zeichenpausen:
 *      die Grenze liegt über der längsten (mal √(7/3), wie Wort zu Zeichen
 *      nach der Norm), nie unter der Vorgabe.
 */
export function pausenGrenzen(
  pausen: readonly number[],
  u: number,
  dehnung = 1,
  unbekannt: (zeichenGrenze: number) => number = () => 0,
): { zeichen: number; wort: number } {
  const d = pausen.filter((x) => Number.isFinite(x) && x > 0).sort((a, b) => a - b);
  // Die Lücke, in der eine Grenze liegt: das Verhältnis der beiden Pausen links und rechts von ihr.
  const lueckeUm = (g: number): number => {
    const i = d.findIndex((x) => x >= g);
    return i > 0 ? d[i]! / d[i - 1]! : 1;
  };
  const vorgabe = Math.sqrt(3 * dehnung) * u;
  const kandidaten = [vorgabe];
  for (let i = 1; i < d.length; i++) {
    if (d[i - 1]! > DEKODER_PAUSE_IM_ZEICHEN_HOECHSTENS * u) break;
    if (d[i]! / d[i - 1]! >= DEKODER_LERN_LUECKE) kandidaten.push(Math.sqrt(d[i - 1]! * d[i]!));
  }
  // Gewählt wird, was die wenigsten unbekannten Zeichen ergibt — bei Gleichstand die deutlichste Lücke.
  let zeichen = vorgabe;
  let bester = { fehl: unbekannt(vorgabe), luecke: lueckeUm(vorgabe) };
  for (const k of kandidaten.slice(1)) {
    const wert = { fehl: unbekannt(k), luecke: lueckeUm(k) };
    if (wert.fehl < bester.fehl || (wert.fehl === bester.fehl && wert.luecke > bester.luecke)) {
      zeichen = k;
      bester = wert;
    }
  }
  const oben = d.filter((x) => x >= zeichen);
  const g = zweiGruppen(oben);
  if (g && g.oben / g.unten >= DEKODER_TRENNUNG) return { zeichen, wort: Math.sqrt(g.unten * g.oben) };
  // Liegen die Pausen weiter als 2 : 1 auseinander, sind Wortpausen darunter, nur dicht an den Zeichenpausen
  // (eine zittrige Hand nach der Norm: Zeichen 2,3–3,8 Punkte, Wörter ab 5,3) — dann trennt die größte Lücke.
  if (oben.length >= 2 && oben[oben.length - 1]! / oben[0]! >= 2) {
    let luecke = 0;
    let wort = Number.POSITIVE_INFINITY;
    for (let i = 1; i < oben.length; i++) {
      const v = oben[i]! / oben[i - 1]!;
      if (v > luecke) {
        luecke = v;
        wort = Math.sqrt(oben[i - 1]! * oben[i]!);
      }
    }
    return { zeichen, wort };
  }
  // Sonst sind es nur Zeichenpausen: die Grenze liegt über der längsten, nie unter der Vorgabe.
  const wortVorgabe = Math.max(DEKODER_WORT_GRENZE * dehnung * u, zeichen * Math.sqrt(7));
  return { zeichen, wort: oben.length ? Math.max(wortVorgabe, oben[oben.length - 1]! * Math.sqrt(7 / 3)) : wortVorgabe };
}

export interface DekoderStand {
  /** Was bisher erkannt ist (Wörter durch ein Leerzeichen getrennt). */
  text: string;
  /** Die Muster des noch offenen Zeichens, z. B. „.-“. */
  offen: string;
  /** Die aktuelle Schätzung der Punktlänge. */
  punktMs: number;
}

/**
 * Der Dekoder für das Geben, Druck für Druck.
 *
 *   - `druck(ms)`: eine Taste war `ms` lang gedrückt.
 *   - `stille(ms)`: seit dem Loslassen sind `ms` vergangen. Darf mit
 *     wachsender Zeit beliebig oft gerufen werden (aus einem Takt) und
 *     beim nächsten Druck mit der echten Pause — es zählt der letzte Wert.
 *
 * Punkt oder Strich: unter √3 Punkten der AKTUELLEN Schätzung ein Punkt.
 * Die Pausen-Grenzen lernt `pausenGrenzen` aus den Pausen der Übung (Vorgabe
 * nach der Norm, gedehnt nach Farnsworth).
 *
 * ⚠️ Der Text wird bei jedem Stand aus ALLEN Drücken und Pausen der Übung
 *    neu gelesen, mit der Schätzung von JETZT. Am Anfang kennt der Dekoder
 *    die Hand nicht: gibt Martin langsamer als eingestellt, wäre schon die
 *    erste Pause innerhalb eines Zeichens „Zeichenende“ — und ein einmal
 *    geschlossenes Zeichen bliebe falsch. So ordnet sich der Anfang selbst,
 *    sobald Punkt und Strich nebeneinander stehen. Preis: ändert sich das
 *    Tempo mitten in einer Übung stark, kann ein früher Teil umspringen.
 */
export class MorseDekoder {
  private druecke: number[] = [];
  private folge: Array<{ an: boolean; ms: number }> = [];
  /** Stille vor einem verworfenen Prellen — die nächste Stille zählt dazu. */
  private stilleBasis = 0;
  private zu = false;
  private u: number;
  private readonly dehnung: number;

  /**
   * `dehnung`: wie stark die Hand die Pausen zwischen Zeichen und Wörtern
   * dehnt (Farnsworth; 1 = Norm, siehe `farnsworthDehnung`). Wer mit gedehnten
   * Pausen hören gelernt hat, gibt auch so — eine Zeichenpause von 4,5 Punkten
   * wäre sonst schon ein Wortende. Die Grenze im Zeichen bleibt: Punkte,
   * Striche und ihre Abstände kommen im vollen Tempo.
   */
  constructor(startPunktMs: number = punktMs(MORSE_VORGABE.tempo), o: { dehnung?: number } = {}) {
    this.u = startPunktMs > 0 && Number.isFinite(startPunktMs) ? startPunktMs : punktMs(MORSE_VORGABE.tempo);
    const d = o.dehnung;
    this.dehnung = typeof d === "number" && Number.isFinite(d) ? Math.min(4, Math.max(1, d)) : 1;
  }

  /** Eine Taste war `ms` lang gedrückt. Prellen (unter 15 ms) zählt nicht — die Stille davor läuft weiter. */
  druck(ms: number): DekoderStand {
    if (!Number.isFinite(ms) || ms < DEKODER_PRELLEN_MS) {
      const letzte = this.folge[this.folge.length - 1];
      if (letzte && !letzte.an && Number.isFinite(ms) && ms > 0) this.stilleBasis = letzte.ms + ms;
      return this.stand();
    }
    this.stilleBasis = 0;
    this.zu = false;
    this.druecke.push(ms);
    if (this.druecke.length > DEKODER_FENSTER * 4) this.druecke.splice(0, this.druecke.length - DEKODER_FENSTER);
    this.folge.push({ an: true, ms });
    this.u = schaetzePunktMs(this.druecke, this.u);
    return this.stand();
  }

  /** Seit dem Loslassen sind `ms` vergangen. Vor dem ersten Druck zählt Stille nicht. */
  stille(ms: number): DekoderStand {
    if (!Number.isFinite(ms) || ms < 0 || this.folge.length === 0) return this.stand();
    const letzte = this.folge[this.folge.length - 1]!;
    const wert = this.stilleBasis + ms;
    if (letzte.an) this.folge.push({ an: false, ms: wert });
    else letzte.ms = wert;
    return this.stand();
  }

  /** Die Übung ist zu Ende: das offene Zeichen gilt als abgeschlossen. */
  abschliessen(): DekoderStand {
    this.zu = true;
    return this.stand();
  }

  /** Den Text vergessen; die Schätzung der Hand bleibt. */
  leeren(): void {
    this.folge = [];
    this.stilleBasis = 0;
    this.zu = false;
  }

  /** Wie viele abgeschlossene Zeichen ergäben bei dieser Zeichengrenze ein Muster, das es nicht gibt? */
  private unbekannteBei(grenze: number, u: number): number {
    let fehl = 0;
    let offen = "";
    const schliessen = (): void => {
      if (offen && MORSE_RUECKWAERTS[offen] === undefined) fehl++;
      offen = "";
    };
    for (const s of this.folge) {
      if (s.an) offen += s.ms < DEKODER_ZEICHEN_GRENZE * u ? "." : "-";
      else if (s.ms >= grenze) schliessen();
    }
    if (this.zu) schliessen();
    return fehl;
  }

  stand(): DekoderStand {
    const u = this.u;
    // Die Grenzen aus den Pausen dieser Übung — ohne die letzte, solange sie noch wächst.
    const letzteOffen = !this.zu && this.folge.length > 0 && !this.folge[this.folge.length - 1]!.an;
    const pausen = this.folge.filter((x, i) => !x.an && !(letzteOffen && i === this.folge.length - 1)).map((x) => x.ms);
    const grenze = pausenGrenzen(pausen, u, this.dehnung, (g) => this.unbekannteBei(g, u));
    const woerter: string[] = [""];
    let offen = "";
    const schliessen = (): void => {
      if (!offen) return;
      woerter[woerter.length - 1] += MORSE_RUECKWAERTS[offen] ?? UNBEKANNT;
      offen = "";
    };
    for (const s of this.folge) {
      if (s.an) {
        offen += s.ms < DEKODER_ZEICHEN_GRENZE * u ? "." : "-";
        continue;
      }
      if (s.ms >= grenze.zeichen) schliessen();
      if (s.ms >= grenze.wort && woerter[woerter.length - 1] !== "") woerter.push("");
    }
    if (this.zu) schliessen();
    return { text: woerter.filter((w) => w !== "").join(" "), offen, punktMs: u };
  }
}

/** Wie stark Farnsworth die Pausen zwischen Zeichen dehnt (1 = keine Dehnung). */
export function farnsworthDehnung(e: Pick<MorseEinstellungen, "tempo" | "wirksam">): number {
  const p = pausen(e.tempo, e.wirksam);
  return p.zeichenMs / (3 * p.punktMs);
}

/** Eine ganze Folge auf einmal: abwechselnd Druck und Stille, beginnend mit einem Druck. */
export function dekodiere(folge: ReadonlyArray<{ an: boolean; ms: number }>, startPunktMs?: number, o: { dehnung?: number } = {}): DekoderStand {
  const d = new MorseDekoder(startPunktMs, o);
  for (const s of folge) {
    if (s.an) d.druck(s.ms);
    else d.stille(s.ms);
  }
  return d.abschliessen();
}
